import { useState, useEffect, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { debounce } from 'lodash';

interface UseFormularioSubmitProps {
  respostaId: string;
  form: UseFormReturn<any>;
  onSubmitSuccess?: (respostaId: string) => void;
  isPublic?: boolean;
  formularioId?: string;
  clientId?: string;
}

export function useFormularioSubmit({ 
  respostaId, 
  form,
  onSubmitSuccess,
  isPublic = false,
  formularioId,
  clientId
}: UseFormularioSubmitProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [currentRespostaId, setCurrentRespostaId] = useState(respostaId);

  const createPublicResposta = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Capturar metadados para resposta anônima
      const metadata: any = {
        user_agent: navigator.userAgent,
        origem: 'link_publico',
        dispositivo: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        tempo_inicio: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('formulario_respostas')
        .insert({
          formulario_id: formularioId,
          respondido_por: user?.id || null, // NULL para anônimo
          status: 'rascunho',
          metadata,
          client_id: clientId || null,
        })
        .select()
        .single();

      if (error) throw error;
      setCurrentRespostaId(data.id);
    } catch (error: any) {
      console.error('Erro ao criar resposta pública:', error);
      toast.error('Erro ao iniciar formulário');
    }
  };


  // Upload de arquivo
  const uploadFile = async (file: File, campoId: string): Promise<string> => {
    if (!currentRespostaId) throw new Error('Resposta não criada ainda');
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${currentRespostaId}/${campoId}/${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('formulario-anexos')
      .upload(fileName, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('formulario-anexos')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  // Capturar localização GPS
  const captureLocation = (): Promise<{ latitude: number; longitude: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalização não suportada pelo navegador'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  // Submeter formulário final - retorna o respostaId criado
  const submitFormulario = async (): Promise<string> => {
    setIsSaving(true);
    try {
      let respostaIdToUse = currentRespostaId;

      // Se não existe resposta ainda, criar agora
      if (!respostaIdToUse) {
        const { data: { user } } = await supabase.auth.getUser();

        const metadata: any = {
          user_agent: navigator.userAgent,
          origem: isPublic ? 'link_publico' : 'portal',
          dispositivo: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          tempo_inicio: new Date().toISOString(),
        };

        const { data, error } = await supabase
          .from('formulario_respostas')
          .insert({
            formulario_id: formularioId,
            respondido_por: user?.id || null,
            status: 'rascunho',
            metadata,
            client_id: clientId || null,
          })
          .select()
          .single();

        if (error) throw error;

        respostaIdToUse = data.id;
        setCurrentRespostaId(data.id);
      }

      // Salvar apenas campos preenchidos
      const values = form.getValues();

      for (const [campoId, valor] of Object.entries(values)) {
        if (campoId === 'metadata') continue;
        
        // Pular campos vazios/não preenchidos
        if (valor === undefined || valor === null || valor === '') continue;
        
        // Para arrays vazios (multiselect, checkboxes)
        if (Array.isArray(valor) && valor.length === 0) continue;
        
        // Para objetos vazios
        if (typeof valor === 'object' && valor !== null && Object.keys(valor).length === 0) continue;

        let valorToSave = valor;
        let valorJson = null;

        if (typeof valor === 'object' && valor !== null) {
          valorJson = valor;
          valorToSave = null;
        }

        const { error } = await supabase
          .from('formulario_campo_respostas')
          .upsert([{
            resposta_id: respostaIdToUse,
            campo_id: campoId,
            valor: valorToSave as string | null,
            valor_json: valorJson,
            respondido_em: new Date().toISOString(),
          }]);

        if (error) throw error;
      }

      // Atualizar status da resposta para concluído
      const { error } = await supabase
        .from('formulario_respostas')
        .update({
          status: 'concluido',
          data_conclusao: new Date().toISOString(),
        })
        .eq('id', respostaIdToUse);

      if (error) throw error;

      toast.success('Formulário enviado com sucesso!');
      
      // Retornar o ID para que o chamador possa verificar triggers
      return respostaIdToUse;
    } catch (error: any) {
      toast.error('Erro ao enviar formulário: ' + error.message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isSaving,
    uploadFile,
    captureLocation,
    submitFormulario,
  };
}
